import {Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild, OnDestroy, OnChanges, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {LOADING_TIMEOUT} from "../../constants/global.constants";
import {NgSelectComponent} from "@ng-select/ng-select";
import {SelectItem} from "../../interface/selectItem";

@Component({
  selector: 'app-scan-multi-select',
  templateUrl: './scan-multi-select.component.html',
  styleUrl: './scan-multi-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScanMultiSelectComponent),
      multi: true
    }
  ]
})
export class ScanMultiSelectComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {

  @ViewChild(NgSelectComponent) ngSelect!: NgSelectComponent;

  @Input() items: SelectItem[] = [];
  @Input() selectedHashtags: SelectItem[] = []
  @Input() maxSelectedItemsCount: number = 20;
  @Input() bindLabel: string = '';
  @Input() bindValue: string = '';
  @Input() notFoundText: string = '';
  @Input() showSeeMoreButton: boolean = false;
  @Input() label: string = '';

  @Output() search: EventEmitter<string> = new EventEmitter<string>();
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  @Output() loadMore: EventEmitter<void> = new EventEmitter<void>();
  @Output() disabledOptionClick: EventEmitter<void> = new EventEmitter<void>();

  selectedItems: SelectItem[] = [];
  isLoading: boolean = false;
  filteredItems: SelectItem[] = [];
  private searchTerm: string = '';

  @Input() closeOnSelect: boolean = false;

  protected readonly IS_MULTIPLE: boolean = true;
  protected readonly IS_CLEARABLE: boolean = false;

  private languageSubscription?: Subscription;

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.setValueIfTagsPresent(this.selectedHashtags);
    this.filteredItems = this.items;

    this.languageSubscription = this.translateService.onLangChange.subscribe(() => {
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filterItems();
    }
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  protected setValue(value: SelectItem[]) {
    const oldSelectedItems = [...this.selectedItems];
    this.verifyMaxSelectedItemsCount();
    this.selectedItems = value;

    if(oldSelectedItems?.length != value?.length) {
      this.onChange(this.selectedItems);
      this.onTouched();
    }
    this.onOpen()
  }

  writeValue(obj: SelectItem[] | string[]): void {
    if (obj) {
      if (obj.length > 0 && typeof obj[0] === 'string') {
        const stringValues = obj as string[];
        const selectedItems = stringValues
          .map(value => this.items.find(item => {
            const bindValue = this.bindValue || 'name';
            const itemValue = (item[bindValue as keyof SelectItem] as string);
            return itemValue === value;
          }))
          .filter((item): item is SelectItem => item !== undefined);
        this.selectedItems = selectedItems;
        this.filterItems();
      } else {
        this.items = obj as SelectItem[];
        this.filterItems();
        this.selectedItems = obj as SelectItem[];
      }
    } else {
      this.selectedItems = [];
    }
    this.setValueIfTagsPresent(this.selectedHashtags);
  }

  registerOnChange(fn: (value: SelectItem[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  isSelectedById(id: bigint): boolean {
    let isSelected: boolean = false;
    this.selectedItems.forEach(item => {
      if (item.id === id) {
        isSelected = true;
      }
    });
    return isSelected;
  }

  verifyMaxSelectedItemsCount() {
    if (this.maxSelectedItemsCount == this.selectedItems.length) {
      this.disabledOptionClick.emit();
    }
  }

  private onChange: (value: SelectItem[]) => void = () => {
  };

  private onTouched: () => void = () => {
  };

  setValueIfTagsPresent(selectedHashtags: SelectItem[]) {
    if (selectedHashtags.length > 0) {
      this.setValue(selectedHashtags);
    }
  }

  compareHashtagsWithId(item: SelectItem, selected: SelectItem) {
    return item.id === selected.id;
  }

  onLoadMore() {
    this.isLoading = true;
    this.loadMore.emit();

    setTimeout(() => {
      this.isLoading = false;
    }, LOADING_TIMEOUT);
  }

  onOpen() {
    if (this.selectedItems.length >= this.maxSelectedItemsCount) {
      this.ngSelect.close();
    }
  }

  onSearch(event: { term: string }) {
    const searchTerm = event?.term || '';
    this.searchTerm = searchTerm;
    this.filterItems();
    this.search.emit(searchTerm);
  }

  private filterItems() {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredItems = [...this.items];
    } else {
      const term = this.searchTerm.toLowerCase().trim();
      this.filteredItems = this.items.filter((item: SelectItem) => {
        const labelValue = (item[this.bindLabel] as string) || '';
        return labelValue.toLowerCase().includes(term);
      });
    }
  }
}
